import { useEffect, useState } from "react";

import { Loader2, Search, UserPlus } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { ScrollArea } from "~/components/ui/scroll-area";
import { toast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import type { Database } from "~/types/supabase";

interface ImportClientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onClientImported: (client?: { id: string; name: string }) => void;
}

type Client = Pick<
  Database["public"]["Tables"]["clients"]["Row"],
  "id" | "name" | "tax_id"
>;

export const ImportClientDialog = ({
  open,
  onOpenChange,
  onClientImported,
}: ImportClientDialogProps) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [filteredClients, setFilteredClients] = useState<Client[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      fetchNonMaquilaClients();
      setSearchQuery("");
    }
  }, [open]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredClients(clients);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredClients(
        clients.filter(
          (c) =>
            c.name?.toLowerCase().includes(query) ||
            c.tax_id?.toLowerCase().includes(query)
        )
      );
    }
  }, [searchQuery, clients]);

  const fetchNonMaquilaClients = async () => {
    setIsLoading(true);
    const { data } = await supabase
      .from("clients")
      .select("id, name, tax_id")
      .eq("is_maquila_client", false)
      .is("deleted_at", null)
      .order("name");
    if (data) {
      setClients(data);
      setFilteredClients(data);
    }
    setIsLoading(false);
  };

  const handleImport = async (client: Client) => {
    setIsImporting(client.id);
    try {
      const { error } = await supabase
        .from("clients")
        .update({ is_maquila_client: true })
        .eq("id", client.id);

      if (error) throw error;

      toast({
        title: "Cliente importado",
        description: `"${client.name}" ha sido agregado a clientes maquila.`,
      });

      onClientImported({ id: client.id, name: client.name ?? "" });
      onOpenChange(false);
    } catch (error) {
      console.error("Error importing client:", error);
      toast({
        title: "Error",
        description: "No se pudo importar el cliente. Intente nuevamente.",
        variant: "destructive",
      });
    } finally {
      setIsImporting(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Importar Cliente Existente</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre o RUT..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          <ScrollArea className="h-[300px] rounded-md border">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : filteredClients.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                <UserPlus className="mb-2 h-8 w-8" />
                <p className="text-sm">
                  {searchQuery
                    ? "No se encontraron clientes"
                    : "No hay clientes disponibles para importar"}
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {filteredClients.map((client) => (
                  <button
                    key={client.id}
                    onClick={() => handleImport(client)}
                    disabled={isImporting !== null}
                    className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-muted/50 disabled:opacity-50"
                  >
                    <div>
                      <p className="font-medium">
                        {client.name || "Sin nombre"}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {client.tax_id || "Sin RUT"}
                      </p>
                    </div>
                    {isImporting === client.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <UserPlus className="h-4 w-4 text-muted-foreground" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </ScrollArea>

          <p className="text-xs text-muted-foreground">
            Los clientes importados seguiran apareciendo en el modulo de ventas.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
